import { Controller } from "@hotwired/stimulus"
import _ from "lodash"

const MAPBOX_GEOCODE_URL = "https://api.mapbox.com/search/geocode/v6/forward"
const MIN_QUERY_LENGTH = 3
const DEBOUNCE_WAIT_MS = 300
const MAX_SUGGESTIONS = 4

export default class extends Controller {
  static targets = [
    "locationInput",
    "suggestionsList",
    "canonicalLocation",
    "suggestionItem",
    "cityInput",
    "stateInput",
    "zipInput"
  ]
  static values = {
    token: String,
    types: { type: String, default: "place" },
    proximity: { type: String, default: "" }
  }

  connect() {
    this.activeIndex = -1
    this.suggestions = []
    this._debouncedFetch = _.debounce(this._fetchSuggestions.bind(this), DEBOUNCE_WAIT_MS)
  }

  disconnect() {
    this._debouncedFetch.cancel()
    this.abortController?.abort()
    if (this._outsideClickHandler) {
      document.removeEventListener("click", this._outsideClickHandler, { capture: true })
    }
  }

  onInput() {
    const query = this.locationInputTarget.value.trim()
    if (this.hasCanonicalLocationTarget) this.canonicalLocationTarget.value = ""
    if (query.length < MIN_QUERY_LENGTH) {
      this._closeDropdown()
      this._debouncedFetch.cancel()
      return
    }
    this._debouncedFetch(query)
  }

  onKeydown(event) {
    if (!this._isDropdownOpen()) return
    switch (event.key) {
      case "ArrowDown": event.preventDefault(); this._moveFocus(1); break
      case "ArrowUp":   event.preventDefault(); this._moveFocus(-1); break
      case "Enter":
        if (this.activeIndex >= 0) {
          event.preventDefault()
          this._selectSuggestion(this.suggestions[this.activeIndex])
          this._closeDropdown()
        }
        break
      case "Escape": this._closeDropdown(); break
    }
  }

  _fetchSuggestions(query) {
    if (!this.tokenValue) return
    if (/^\d+$/.test(query)) return
    this.abortController?.abort()
    this.abortController = new AbortController()

    const params = new URLSearchParams({
      q: query,
      access_token: this.tokenValue,
      types: this.typesValue,
      country: "us",
      limit: MAX_SUGGESTIONS
    })
    if (this.proximityValue) params.set("proximity", this.proximityValue)

    fetch(`${MAPBOX_GEOCODE_URL}?${params}`, {
      signal: this.abortController.signal,
      headers: { Accept: "application/json" }
    })
      .then(r => { if (!r.ok) throw new Error(`Mapbox HTTP ${r.status}`); return r.json() })
      .then(data => { this.suggestions = data.features || []; this._renderDropdown() })
      .catch(err => { if (err.name !== "AbortError") this._closeDropdown() })
  }

  _renderDropdown() {
    const dropdown = this.suggestionsListTarget
    dropdown.innerHTML = ""

    if (this.suggestions.length === 0) { this._closeDropdown(); return }

    this.activeIndex = -1
    this.suggestions.forEach((feature) => {
      const item = document.createElement("button")
      item.type = "button"
      item.className = "dropdown-item"
      item.dataset.locationAutocompleteTarget = "suggestionItem"
      item.textContent = this._labelFor(feature)
      item.addEventListener("mousedown", event => event.preventDefault())
      item.addEventListener("click", () => { this._selectSuggestion(feature); this._closeDropdown() })
      dropdown.appendChild(item)
    })

    dropdown.classList.add("show")
    dropdown.removeAttribute("hidden")
    this.locationInputTarget.setAttribute("aria-expanded", "true")

    if (this._outsideClickHandler) {
      document.removeEventListener("click", this._outsideClickHandler, { capture: true })
    }
    this._outsideClickHandler = e => { if (!this.element.contains(e.target)) this._closeDropdown() }
    document.addEventListener("click", this._outsideClickHandler, { capture: true })
  }

  _selectSuggestion(feature) {
    const canonical = this._labelFor(feature)
    if (this.hasCityInputTarget && this.hasStateInputTarget && this.hasZipInputTarget) {
      const context = feature.properties.context || {}
      this.locationInputTarget.value = feature.properties.name || ""
      this.cityInputTarget.value = context.place?.name || ""
      this.stateInputTarget.value = context.region?.region_code || ""
      this.zipInputTarget.value = context.postcode?.name || ""
    } else {
      this.locationInputTarget.value = canonical
    }
    if (this.hasCanonicalLocationTarget) this.canonicalLocationTarget.value = canonical
    this.abortController?.abort()
    this.locationInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  _labelFor(feature) {
    const props = feature.properties
    if (props.full_address) return props.full_address

    const regionCode = props.context?.region?.region_code
    return regionCode ? `${props.name}, ${regionCode}` : props.name
  }

  _moveFocus(direction) {
    const items = this.suggestionItemTargets
    if (!items.length) return
    this.activeIndex = _.clamp(this.activeIndex + direction, -1, items.length - 1)
    items.forEach((item, i) => {
      item.classList.toggle("active", i === this.activeIndex)
      i === this.activeIndex
        ? item.setAttribute("aria-selected", "true")
        : item.removeAttribute("aria-selected")
    })
  }

  _isDropdownOpen() {
    return this.suggestionsListTarget.classList.contains("show")
  }

  _closeDropdown() {
    this.suggestionsListTarget.classList.remove("show")
    this.suggestionsListTarget.setAttribute("hidden", "")
    this.locationInputTarget.setAttribute("aria-expanded", "false")
    this.activeIndex = -1
    if (this._outsideClickHandler) {
      document.removeEventListener("click", this._outsideClickHandler, { capture: true })
      this._outsideClickHandler = null
    }
  }
}
